import React, { createContext, useContext } from 'react';
import { Helmet } from 'react-helmet-async';

import { addRequestInterceptor, addResponseInterceptor, isApiError, PathSchema, StatusSchema } from '@/js/libs/api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNavigate, type NavigateFunction } from '@/js/libs/router';
import { Loader } from '@/js/libs/ui';
import { useToken, useGetIdentity, useIdentityUser } from '@/js/libs/use-auth';

import { onTranslationListOrAddMissing } from 'src/api';
import { useForcedLogout } from 'src/hooks/useForcedLogout';
import * as companySwitchConstants from 'src/modules/CompaniesSwitch/constants';
import { useCompanySwitchContext } from 'src/modules/CompaniesSwitch/context';

const ALLOWED_HOSTS = ['adconsole.ch', 'www.adconsole.ch', 'dev.adconsole.ch', 'dev.adconsole.com', 'app.adconsole.com'];

const isGetTokenError = (error: unknown) => isApiError(error, PathSchema) && error.response?.data.path === '/api/token';

const isGetTokenRequest = (url: string) => url === '/api/ws-companies/token';

interface AuthContextType {
  headers: Record<string, string>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const IdentityIntegration: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { company } = useCompanySwitchContext();
  const companyIdState = React.useMemo(() => ({ id: company?.id }), [company]);

  const userParams = React.useMemo(() => ({ selectedCompanyId: companyIdState.id }), [companyIdState.id]);

  const { getIdentity, loading, initialized } = useGetIdentity({
    userParams,
  });

  React.useEffect(() => {
    if (companyIdState) {
      // wait until request interceptors are applied
      setTimeout(() => getIdentity(), 0);
    }
  }, [companyIdState, getIdentity]);

  const user = useIdentityUser();

  if (loading || !initialized) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        {ALLOWED_HOSTS.includes(window.location.hostname) && user?.company?.id && (
          <script type='text/javascript' id='usersnap'>
            {`
              (function initUsersnap() {
                window.onUsersnapLoad = function (api) {
                  window.Usersnap = api;
                  api.init({
                    user: {
                      userId: '${user.id}',
                      email: '${user.email}',
                    },
                  });
                };
                var script = document.createElement('script');
                script.defer = 1;
                script.src = 'https://widget.usersnap.com/global/load/549d1dec-b5cd-4f6d-9f2e-5a625172662a?onload=onUsersnapLoad';
                document.getElementsByTagName('head')[0].appendChild(script);
              }());
            `}
          </script>
        )}
      </Helmet>
      {children}
    </>
  );
};

interface AxiosIntegrationProps extends React.PropsWithChildren {
  headers: Record<string, string>;
}

const AxiosIntegration: React.FC<AxiosIntegrationProps> = ({ children, headers }) => {
  const [isInterceptorSet, setIsInterceptorSet] = React.useState<boolean>(false);
  const navigateRef = useCallbackRef<NavigateFunction>(useNavigate());
  const onForcedLogout = useForcedLogout();

  React.useEffect(() => {
    const headersInterceptorUnsubscribe = addRequestInterceptor({
      async onFulfilled(request) {
        if (onTranslationListOrAddMissing(request.url) || isGetTokenRequest(request.url)) {
          // remove Authorization header for translations and auth requests
          const { Authorization: _Authorization, ...restHeaders } = headers;
          request.headers = {
            ...request.headers,
            ...restHeaders,
          };
        } else {
          request.headers = {
            ...request.headers,
            ...headers,
          };
        }
        return request;
      },
    });

    const sentryInterceptorUnsubscribe = addResponseInterceptor({
      onRejected(err) {
        if (isApiError(err, StatusSchema) && err.response) {
          if (err.response.status === 401 && !isGetTokenError(err)) {
            onForcedLogout();
          }

          if (err.response.status === 403 && window.location.pathname !== '/403' && window.location.pathname !== '/maintenance') {
            navigateRef('/403');
          }

          if (err.response.status === 404 && window.location.pathname !== '/404') {
            navigateRef('/404');
          }
        }
      },
    });

    setIsInterceptorSet(true);

    return () => {
      setIsInterceptorSet(false);
      headersInterceptorUnsubscribe();
      sentryInterceptorUnsubscribe();
    };
  }, [headers, navigateRef, onForcedLogout]);

  if (!isInterceptorSet) {
    return null;
  }

  return <>{children}</>;
};

const AuthContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [token] = useToken();
  const company = useCompanySwitchContext((state) => state.company);
  const headers = React.useMemo(() => {
    const ret: Record<string, string> = {
      'Cache-Control': 'no-cache',
      'If-Modified-Since': 'Sun, 06 Oct 2013 01:16:45 GMT',
    };
    if (token) {
      ret.Authorization = `Bearer ${token}`;
    }
    if (company) {
      ret[companySwitchConstants.REQUEST_HEADER_KEY] = window.btoa(
        JSON.stringify({
          id: company.id,
          companyUUID: company.companyUUID,
        }),
      );
    }

    return ret;
  }, [company, token]);

  const value = React.useMemo<AuthContextType>(
    () => ({
      headers,
    }),
    [headers],
  );

  return (
    <AuthContext.Provider value={value}>
      <AxiosIntegration headers={headers}>
        <IdentityIntegration>{children}</IdentityIntegration>
      </AxiosIntegration>
    </AuthContext.Provider>
  );
};

const useAuthContext = (): AuthContextType => {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('`useAuthContext` is used outside of `AuthContextProvider`');
  }
  return value;
};

export { useAuthContext, AuthContextProvider };
